import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, ShieldOff, Copy, ShieldAlert } from "lucide-react";

/**
 * Renders a QR code from an otpauth URI using an inline SVG via Google Chart API alternative.
 * We use a public-domain QR renderer library-free by drawing a QR grid via a data URL.
 * For simplicity here we display the URI in plaintext + a copyable secret; users can scan
 * the secret manually via Google Authenticator (accepted format).
 */
function QRDisplay({ uri, secret }) {
  return (
    <div className="p-4 border border-border rounded-md bg-accent/30 space-y-3">
      <div className="text-xs text-muted-foreground">امسح الرمز أدناه في تطبيق Google Authenticator أو Microsoft Authenticator:</div>
      <div className="flex items-center justify-center">
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(uri)}&size=200x200&margin=5`}
          alt="QR Code"
          className="rounded border border-border bg-white p-1"
          data-testid="totp-qr"
        />
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1">أو أدخل هذا المفتاح يدوياً:</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 p-2 bg-background rounded font-mono text-xs break-all" data-testid="totp-secret">{secret}</code>
          <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(secret); toast.success("تم النسخ"); }}><Copy className="w-3 h-3" /></Button>
        </div>
      </div>
    </div>
  );
}

export default function TwoFactorSetup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const enrollMode = new URLSearchParams(location.search).get("enroll") === "1";
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [policy, setPolicy] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  const loadStatus = async () => {
    try { const r = await api.get("/auth/2fa/status"); setStatus(r.data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadPolicy = async () => {
    try { const r = await api.get("/auth/2fa/policy"); setPolicy(r.data); }
    catch { /* non-root or unavailable */ }
  };
  useEffect(() => { loadStatus(); if (user?.is_root) loadPolicy(); }, []); // eslint-disable-line

  const togglePolicy = async (val) => {
    try {
      await api.put("/auth/2fa/policy", { require_privileged_2fa: val, required_roles: ["super_admin", "company_manager"] });
      setPolicy(p => ({ ...(p || {}), require_privileged_2fa: val }));
      toast.success(val ? "تم إلزام المدراء بتفعيل 2FA" : "تم إلغاء الإلزام");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const doSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
      toast.info("امسح الرمز وأدخل الكود لتفعيل 2FA");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const doEnable = async () => {
    if (!code || code.length !== 6) { toast.error("الرمز يجب أن يكون 6 أرقام"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      toast.success("تم تفعيل 2FA بنجاح!");
      setSetup(null); setCode("");
      if (data.recovery_codes) { setRecoveryCodes(data.recovery_codes); return; }
      if (enrollMode) { setTimeout(() => navigate("/"), 600); return; }
      loadStatus();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const doRegenerate = async () => {
    const pw = prompt("لتوليد رموز استرداد جديدة (وإلغاء القديمة)، أدخل كلمة المرور:");
    if (!pw) return;
    setLoading(true);
    try {
      const { data } = await api.post("/auth/2fa/recovery-codes/regenerate", { email: user?.email || "", password: pw });
      setRecoveryCodes(data.recovery_codes);
      toast.success("تم توليد رموز جديدة — احفظها");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const doDisable = async () => {
    const c = prompt("أدخل رمز 2FA لتعطيلها:");
    if (!c || c.length !== 6) return;
    setLoading(true);
    try {
      await api.post("/auth/2fa/disable", { code: c });
      toast.success("تم تعطيل 2FA");
      loadStatus();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="2fa-page">
      {recoveryCodes && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="recovery-codes-modal">
          <Card className="p-6 max-w-md w-full space-y-4">
            <div className="font-bold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary" /> رموز الاسترداد</div>
            <p className="text-sm text-muted-foreground">احفظ هذه الرموز في مكان آمن. كل رمز يُستخدم مرة واحدة للدخول عند فقدان هاتفك. <b>لن تظهر مرة أخرى.</b></p>
            <div className="grid grid-cols-2 gap-2 p-3 bg-accent/40 rounded font-mono text-sm">
              {recoveryCodes.map((c, i) => <div key={i} data-testid={`recovery-code-${i}`}>{c}</div>)}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 gap-1" onClick={() => { navigator.clipboard.writeText(recoveryCodes.join("\n")); toast.success("تم النسخ"); }}><Copy className="w-4 h-4" /> نسخ</Button>
              <Button className="flex-1" data-testid="recovery-codes-done" onClick={() => { setRecoveryCodes(null); if (enrollMode) navigate("/"); else loadStatus(); }}>حفظتها — متابعة</Button>
            </div>
          </Card>
        </div>
      )}
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><KeyRound className="w-7 h-7 text-primary" /> المصادقة الثنائية (2FA)</h1>
        <p className="text-sm text-muted-foreground mt-1">حماية إضافية لحسابك — رمز 6 أرقام يتغيّر كل 30 ثانية عبر تطبيق مثل Google Authenticator</p>
      </div>

      {enrollMode && !status?.enabled && (
        <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-950/30 border-2 border-red-300 rounded-md" data-testid="2fa-enroll-required">
          <ShieldAlert className="w-8 h-8 text-red-600 shrink-0" />
          <div>
            <div className="font-bold text-red-800 dark:text-red-300">تفعيل المصادقة الثنائية مطلوب</div>
            <div className="text-xs text-red-700 dark:text-red-400 mt-1">قام المدير الرئيسي بإلزام حسابات المدراء بتفعيل 2FA. أكمل الإعداد أدناه للمتابعة إلى النظام.</div>
          </div>
        </div>
      )}

      {user?.is_root && (
        <Card className="p-5" data-testid="2fa-policy-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-bold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" /> إلزام المدراء بالمصادقة الثنائية</div>
              <div className="text-xs text-muted-foreground mt-1">عند التفعيل، يُطلب من كل مدير عام ومدير شركة تفعيل 2FA قبل استخدام النظام. المدير الرئيسي مستثنى دائماً لضمان عدم الإغلاق.</div>
            </div>
            <Switch
              checked={!!policy?.require_privileged_2fa}
              onCheckedChange={togglePolicy}
              data-testid="2fa-policy-toggle"
            />
          </div>
        </Card>
      )}

      <Card className="p-6">
        {status?.enabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 rounded-md">
              <ShieldCheck className="w-8 h-8 text-emerald-600 shrink-0" />
              <div>
                <div className="font-bold text-emerald-800 dark:text-emerald-300">2FA مفعّلة</div>
                <div className="text-xs text-emerald-700 dark:text-emerald-400">حسابك محمي بمستوى إضافي</div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={doDisable} disabled={loading} className="gap-2" data-testid="2fa-disable">
                <ShieldOff className="w-4 h-4" /> تعطيل 2FA
              </Button>
              <Button variant="outline" onClick={doRegenerate} disabled={loading} className="gap-2" data-testid="2fa-regenerate">
                <KeyRound className="w-4 h-4" /> توليد رموز استرداد جديدة
              </Button>
            </div>
          </div>
        ) : setup ? (
          <div className="space-y-4">
            <QRDisplay uri={setup.otpauth_uri} secret={setup.secret} />
            <div>
              <label className="text-sm font-bold">أدخل الرمز المكوّن من 6 أرقام لتأكيد الإعداد:</label>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="mt-1 text-center text-xl tracking-widest font-mono"
                placeholder="000000"
                maxLength={6}
                data-testid="totp-code-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={doEnable} disabled={loading || code.length !== 6} className="flex-1 gap-2" data-testid="2fa-enable">
                <ShieldCheck className="w-4 h-4" /> تفعيل 2FA
              </Button>
              <Button variant="outline" onClick={() => { setSetup(null); setCode(""); }}>إلغاء</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-md">
              <ShieldOff className="w-8 h-8 text-amber-600 shrink-0" />
              <div>
                <div className="font-bold text-amber-800 dark:text-amber-300">2FA غير مفعّلة</div>
                <div className="text-xs text-amber-700 dark:text-amber-400 mt-1">حسابك يعتمد فقط على كلمة السر. نوصي بتفعيل 2FA لحماية إضافية.</div>
              </div>
            </div>
            <div className="text-sm space-y-2 text-muted-foreground">
              <div className="font-bold text-foreground">كيف تستخدمها؟</div>
              <ol className="list-decimal list-inside space-y-1 text-xs">
                <li>نزّل تطبيق Google Authenticator أو Microsoft Authenticator من متجر تطبيقات هاتفك</li>
                <li>اضغط "بدء الإعداد" أدناه لتوليد رمز QR</li>
                <li>امسح الرمز بالتطبيق</li>
                <li>أدخل الرمز الظاهر في التطبيق (6 أرقام) لتأكيد التفعيل</li>
              </ol>
            </div>
            <Button onClick={doSetup} disabled={loading} className="gap-2 w-full" data-testid="2fa-setup">
              <KeyRound className="w-4 h-4" /> بدء الإعداد
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}
